import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import Widget from './Widget';
import api from '../../api/api';

/**
 * AISystemWidget - Shows AI systems with their schedules and on/off toggles.
 *
 * Uses the /ai/expert/api/systems/ and /ai/expert/api/schedules/ endpoints.
 */
const AISystemWidget = ({ hideTitle = false }) => {
  const navigate = useNavigate();
  const [systems, setSystems] = useState([]);
  const [schedules, setSchedules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [togglingId, setTogglingId] = useState(null); // track which system is being toggled

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const [systemsRes, schedulesRes] = await Promise.all([
        api.get('/ai/expert/api/systems/', { params: { page_size: 50 } }).catch((err) => { console.error('AISystemWidget: systems fetch failed', err.response?.status, err.response?.data || err.message); return null; }),
        api.get('/ai/expert/api/schedules/', { params: { page_size: 100 } }).catch((err) => { console.error('AISystemWidget: schedules fetch failed', err.response?.status, err.response?.data || err.message); return null; }),
      ]);
      // api interceptor unwraps .data, so response is the paginated object directly
      const systemsList = systemsRes?.results ?? (Array.isArray(systemsRes) ? systemsRes : []);
      const schedulesList = schedulesRes?.results ?? (Array.isArray(schedulesRes) ? schedulesRes : []);
      setSystems(systemsList);
      setSchedules(schedulesList);
    } catch (err) {
      console.error('AISystemWidget fetch error:', err);
      setError('Failed to load AI systems');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Toggle system active state via PATCH
  const toggleSystem = async (sys) => {
    const newActive = !sys.is_active;
    setTogglingId(sys.id);
    try {
      await api.patch(`/ai/expert/api/systems/${sys.id}/`, { is_active: newActive });
      setSystems((prev) =>
        prev.map((s) => (s.id === sys.id ? { ...s, is_active: newActive } : s))
      );
    } catch (err) {
      console.error('Failed to toggle system:', err);
    } finally {
      setTogglingId(null);
    }
  };

  // Toggle schedule active state via activate/deactivate endpoint
  const toggleSchedule = async (sched) => {
    const action = sched.is_active ? 'deactivate' : 'activate';
    setTogglingId(`sched-${sched.id}`);
    try {
      await api.post(`/ai/expert/api/schedules/${sched.id}/${action}/`);
      setSchedules((prev) =>
        prev.map((s) => (s.id === sched.id ? { ...s, is_active: !s.is_active } : s))
      );
    } catch (err) {
      console.error('Failed to toggle schedule:', err);
    } finally {
      setTogglingId(null);
    }
  };

  // Group schedules by system
  const schedulesBySystem = {};
  schedules.forEach((s) => {
    const sysId = s.ai_system;
    if (!schedulesBySystem[sysId]) schedulesBySystem[sysId] = [];
    schedulesBySystem[sysId].push(s);
  });

  const activeSystems = systems.filter((s) => s.is_active).length;

  const formatSchedule = (sched) => {
    if (sched.type === 'RECURRING' || sched.type_display === 'Recurring') {
      const val = sched.interval_every ?? sched.recurring_value;
      const unit = sched.interval_period ?? sched.recurring_unit ?? '';
      return val ? `Every ${val} ${unit}` : 'Recurring';
    }
    if (sched.start_at) {
      return new Date(sched.start_at).toLocaleString(undefined, {
        month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit',
      });
    }
    return sched.type_display || sched.type || '—';
  };

  return (
    <Widget title="AI Systems" hideTitle={hideTitle} className="h-full">
      <div className="h-full flex flex-col overflow-hidden">
        {loading && !systems.length ? (
          <div className="flex-1 flex items-center justify-center text-textSecondary text-sm">Loading…</div>
        ) : error ? (
          <div className="flex-1 flex flex-col items-center justify-center gap-2">
            <span className="text-destructive text-sm">{error}</span>
            <button className="btn btn-neutral btn-xs" onClick={fetchData}>Retry</button>
          </div>
        ) : (
          <>
            {/* Summary counters */}
            <div className="grid grid-cols-3 gap-px bg-borderDefault">
              <div className="bg-backgroundSurface px-3 py-2.5 text-center">
                <div className="text-lg font-bold text-primary">{systems.length}</div>
                <div className="text-[10px] text-textSecondary">Systems</div>
              </div>
              <div className="bg-backgroundSurface px-3 py-2.5 text-center">
                <div className="text-lg font-bold text-success">{activeSystems}</div>
                <div className="text-[10px] text-textSecondary">Active</div>
              </div>
              <div className="bg-backgroundSurface px-3 py-2.5 text-center">
                <div className="text-lg font-bold text-primary">{schedules.length}</div>
                <div className="text-[10px] text-textSecondary">Schedules</div>
              </div>
            </div>

            {/* Systems list with schedules */}
            <div className="flex-1 overflow-y-auto">
              {systems.length === 0 ? (
                <div className="flex items-center justify-center h-full text-sm text-textSecondary">
                  No AI systems configured
                </div>
              ) : (
                <ul className="divide-y divide-borderDefault">
                  {systems.slice(0, 10).map((sys) => {
                    const sysSchedules = schedulesBySystem[sys.id] || [];
                    return (
                      <li key={sys.id} className="px-3 py-2">
                        {/* System row */}
                        <div className="flex items-center justify-between gap-2">
                          <div
                            className="min-w-0 flex-1 cursor-pointer hover:text-primary transition"
                            onClick={() => navigate(`/ai/systems/${sys.id}`)}
                          >
                            <div className="text-sm font-medium text-textPrimary truncate">{sys.name}</div>
                            {sys.description && (
                              <div className="text-[10px] text-textSecondary truncate">{sys.description}</div>
                            )}
                          </div>
                          <button
                            className={`relative inline-flex h-5 w-9 flex-shrink-0 rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-primary/40 focus:ring-offset-1 focus:ring-offset-backgroundSurface ${
                              sys.is_active ? 'bg-success' : 'bg-gray-600'
                            } ${togglingId === sys.id ? 'opacity-50' : 'cursor-pointer'}`}
                            disabled={togglingId === sys.id}
                            onClick={() => toggleSystem(sys)}
                            role="switch"
                            aria-checked={sys.is_active}
                            title={sys.is_active ? 'Deactivate system' : 'Activate system'}
                          >
                            <span
                              className={`pointer-events-none inline-block h-4 w-4 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                                sys.is_active ? 'translate-x-4' : 'translate-x-0'
                              }`}
                            />
                          </button>
                        </div>
                        {/* Schedules for this system */}
                        {sysSchedules.length > 0 && (
                          <div className="mt-1.5 ml-2 space-y-1">
                            {sysSchedules.map((sched) => (
                              <div key={sched.id} className="flex items-center justify-between gap-2 text-[11px]">
                                <div className="flex items-center gap-1.5 min-w-0">
                                  <span className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${sched.is_active ? 'bg-success' : 'bg-gray-500'}`} />
                                  <span className="text-textSecondary truncate">{formatSchedule(sched)}</span>
                                </div>
                                <button
                                  className={`relative inline-flex h-4 w-7 flex-shrink-0 rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-primary/40 focus:ring-offset-1 focus:ring-offset-backgroundSurface ${
                                    sched.is_active ? 'bg-success' : 'bg-gray-600'
                                  } ${togglingId === `sched-${sched.id}` ? 'opacity-50' : 'cursor-pointer'}`}
                                  disabled={togglingId === `sched-${sched.id}`}
                                  onClick={() => toggleSchedule(sched)}
                                  role="switch"
                                  aria-checked={sched.is_active}
                                  title={sched.is_active ? 'Deactivate schedule' : 'Activate schedule'}
                                >
                                  <span
                                    className={`pointer-events-none inline-block h-3 w-3 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                                      sched.is_active ? 'translate-x-3' : 'translate-x-0'
                                    }`}
                                  />
                                </button>
                              </div>
                            ))}
                          </div>
                        )}
                      </li>
                    );
                  })}
                </ul>
              )}
            </div>

            {/* Footer links */}
            <div className="px-3 py-2 border-t border-borderDefault flex items-center justify-between">
              <button
                className="text-xs text-primary hover:underline"
                onClick={() => navigate('/ai/systems')}
              >
                All Systems →
              </button>
              <button
                className="text-xs text-primary hover:underline"
                onClick={() => navigate('/ai/schedules')}
              >
                Schedules →
              </button>
            </div>
          </>
        )}
      </div>
    </Widget>
  );
};

export default AISystemWidget;
